import { head, isEmpty } from 'lodash'
import { connectionConfig as connectionConfigDef } from '@wix/wix-data-client-common/dist/esm/panels/panelDefs'
import { getRepeaterAncestor } from '../../business-logic/components/getRepeaterAncestor'
import getPanelUrl from '@wix/wix-data-client-common/dist/esm/panels/getPanelUrl'
import {
  COLUMN_ROLE,
  REPEATER_ROLE,
} from '@wix/wix-data-client-common-standalone'
import { ComponentTypes } from '@wix/wix-data-client-common'
import {
  logOpenConnectionPanelToBi,
  logOpenFilterCapableComponentConnectionPanelToBi,
} from '../../worker/workerBiEventsLogger'
import { getComponentTypeData } from '../../business-logic/components/getComponentTypeData'
import { getBindingDefinitions } from '../../business-logic/bindings/componentBindingInfo'
import {
  CHOOSE_SCREEN_HEIGHT,
  FILTER_PANEL_HEIGHT,
  NO_DATASETS_ADDITIONAL_HEIGHT,
} from './consts'
import { getUserFilterSection, getUserInputFilterConnection } from './utils'

const openConnectPanel = async (
  {
    hostname,
    appApi,
    editorSdkProxy,
    experimentsManager,
    logger,
    i18n,
    editorType,
    initiator,
    origin,
  },
  { componentRef: originalComponentRef },
) => {
  appApi.disallowCollectionSchemaRefresh()
  try {
    let repeaterAncestor = null

    const {
      sdkType: originalComponentSdkType,
      viewerType: originalComponentViewerType,
    } = await getComponentTypeData({
      editorSdkProxy,
      componentRef: originalComponentRef,
    })

    // workaround fix for repeater child connect panel closing when connecting repeater in live preview
    // fix is applied only when in live preview and repeater is not connected to reduce potential UX issues in irrelevant cases
    repeaterAncestor = await getRepeaterAncestor({
      componentRef: originalComponentRef,
      editorSdkProxy,
    })

    if (repeaterAncestor) {
      const { componentRef: repeaterAncestorRef } = repeaterAncestor
      const repeaterConnections =
        await appApi.fetchComponentBindings(repeaterAncestorRef)
      const repeaterIsConnectedToDataset = Boolean(
        repeaterConnections.find(
          connection => connection.role === REPEATER_ROLE,
        ),
      )

      if (repeaterIsConnectedToDataset) {
        repeaterAncestor = null
      }
    }

    const repeaterComponentRef = repeaterAncestor
      ? repeaterAncestor.componentRef
      : null
    let componentRef = null
    if (!repeaterAncestor) {
      componentRef =
        originalComponentSdkType === ComponentTypes.StripColumnsContainer &&
        (await stripHasNoBackgroundConnection({
          editorSdkProxy,
          stripRef: originalComponentRef,
        }))
          ? head(
              await editorSdkProxy.components.getChildren({
                componentRef: originalComponentRef,
              }),
            )
          : originalComponentRef
    } else {
      componentRef = repeaterComponentRef
    }

    if (!componentRef) {
      return
    }

    logOpenConnectionPanelToBi(logger, editorSdkProxy, {
      componentRef,
      componentType: originalComponentViewerType,
    })

    const componentType = repeaterComponentRef
      ? ComponentTypes.Repeater
      : (
          await getComponentTypeData({
            editorSdkProxy,
            componentRef: originalComponentRef,
          })
        ).sdkType

    await getBindingDefinitions({
      editorSdkProxy,
      experimentsManager,
      componentRef,
      componentType,
      componentViewerType: originalComponentViewerType,
      appApi,
    }).then(result =>
      result.matchWith({
        Nothing: () =>
          logger.info(
            `Could not retrieve binding definitions for component of type ${componentType}`,
            { componentRef },
          ),
        Just: ({ value: bindingDefinitions }) =>
          appApi
            .fetchConnectableDatasets(componentRef)
            .then(datasets =>
              openPanel(
                { hostname, editorSdkProxy, i18n, logger },
                {
                  componentRef,
                  originalComponentRef,
                  componentType,
                  viewerComponentType: originalComponentViewerType,
                  datasets,
                  bindingDefinitions,
                  experimentsManager,
                  initiator,
                  origin,
                  editorType,
                },
              ),
            )
            .catch(error => {
              logger.error(
                new Error('Failed to open connection config panel', {
                  cause: error,
                }),
              )
            }),
      }),
    )
  } finally {
    appApi.allowCollectionSchemaRefresh()
  }
}

const getInitialPanelHeight = async ({
  editorSdkProxy,
  componentRef,
  baseHeight,
  datasets,
  hasRepeaterAncestor,
  bindingDefinitions,
}) => {
  const connections = (
    await editorSdkProxy.components.get({
      componentRefs: [componentRef],
      properties: ['connections'],
    })
  )[0].connections

  const isComponentFilterable = !!getUserFilterSection(bindingDefinitions)

  const isComponentConnectedToUserFilter = getUserInputFilterConnection({
    bindingDefinitions,
    connections,
  })

  if (isComponentFilterable && isEmpty(connections) && !hasRepeaterAncestor) {
    return CHOOSE_SCREEN_HEIGHT
  } else if (isComponentConnectedToUserFilter) {
    return FILTER_PANEL_HEIGHT
  } else {
    return (
      baseHeight + (datasets.length === 0 ? NO_DATASETS_ADDITIONAL_HEIGHT : 0)
    )
  }
}

const getHelpId = async ({
  editorSdkProxy,
  componentRef,
  hasRepeaterAncestor,
  bindingDefinitions,
}) => {
  if (!bindingDefinitions.panel.chooseScreenHelpId) {
    return bindingDefinitions.panel.helpId
  }

  const connections = (
    await editorSdkProxy.components.get({
      componentRefs: [componentRef],
      properties: ['connections'],
    })
  )[0].connections

  const isComponentConnectedToUserFilter = getUserInputFilterConnection({
    bindingDefinitions,
    connections,
  })

  if (isEmpty(connections) && !hasRepeaterAncestor) {
    return bindingDefinitions.panel.chooseScreenHelpId
  } else if (isComponentConnectedToUserFilter) {
    return bindingDefinitions.panel.userInputFilterHelpId
  } else {
    return bindingDefinitions.panel.submitContentHelpId
  }
}

async function openPanel(
  { hostname, editorSdkProxy, i18n, logger },
  {
    componentRef,
    originalComponentRef,
    componentType,
    viewerComponentType,
    datasets,
    bindingDefinitions,
    initiator,
    origin,
    editorType,
  },
) {
  const hasRepeaterAncestor =
    await editorSdkProxy.components.isRepeatedComponent({ componentRef })

  const componentCanBeFilter = bindingDefinitions.sections?.some(
    section => section.isUserInputFilter,
  )
  if (componentCanBeFilter) {
    logOpenFilterCapableComponentConnectionPanelToBi(logger, editorSdkProxy, {
      componentRef,
      componentType,
    })
  }

  const panelTitle = hasRepeaterAncestor
    ? 'Connect_Panel_Title_Repeater'
    : bindingDefinitions.panel.title
  const baseHeight = hasRepeaterAncestor ? 525 : bindingDefinitions.panel.height

  const initialLoadTimeForBI = Date.now()
  const panelResult = await editorSdkProxy.editor.openComponentPanel({
    componentRef,
    title: i18n.t(panelTitle),
    url: await getPanelUrl({
      hostname,
      relativeUrl: connectionConfigDef.url,
      editorSdkProxy,
    }),
    initialData: {
      componentRef,
      originalComponentRef,
      bindingDefinitions,
      componentType,
      viewerComponentType,
      initialLoadTimeForBI,
      datasets,
      initiator,
      origin,
      editorType,
    },
    width: 288,
    height: await getInitialPanelHeight({
      editorSdkProxy,
      componentRef,
      baseHeight,
      datasets,
      hasRepeaterAncestor,
      bindingDefinitions,
    }),
    helpId: await getHelpId({
      editorSdkProxy,
      componentRef,
      hasRepeaterAncestor,
      bindingDefinitions,
    }),
    type: 'connect',
  })
  editorSdkProxy.editor.selection.clearHighlights()
  return panelResult
}

async function stripHasNoBackgroundConnection({ editorSdkProxy, stripRef }) {
  const [{ connections: stripConnections }] =
    await editorSdkProxy.components.get({
      componentRefs: [stripRef],
      properties: ['connections'],
    })
  return stripConnections.every(({ role }) => role !== COLUMN_ROLE)
}

export { openConnectPanel }
